import axios from 'axios';
import { supabase } from '../lib/supabase';

export const secureApi = {
  // Get projects' shared status (whether each project has collaborators)
  getProjectsSharedStatus: async (projectIds: string[]) => {
    try {
      // Get current session for the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw new Error(`Session error: ${sessionError.message}`);
      }

      if (!session?.access_token) {
        throw new Error('No authentication token available');
      }

      // Get the function URL from the Supabase project
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-projects-shared-status`;

      // Call the Edge Function with the project IDs
      const response = await axios.post(
        functionUrl,
        {
          projectIds,
        },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Return the shared status map, with a fallback to empty object
      return response.data?.projectSharedStatus || {};
    } catch (error) {
      console.error('Error fetching projects shared status:', error);
      // Return empty object on error
      return {};
    }
  },

  // Get full dashboard content (tasks, projects and users for filtering)
  getDashboardContent: async () => {
    try {
      // Get current session for the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw new Error(`Session error: ${sessionError.message}`);
      }

      if (!session?.access_token) {
        throw new Error('No authentication token available');
      }

      // Get the function URL from the Supabase project
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-dashboard-content`;

      // Get the client's current date for consistent timezone handling
      const clientToday = new Date();
      const clientTodayStr = clientToday.toISOString();

      // Call the Edge Function with the client's current date
      const response = await axios.post(
        functionUrl,
        {
          // Pass the client-side today date to ensure consistent behavior with frontend
          todayDate: clientTodayStr,
        },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Return the full dashboard content, with fallbacks for each property
      const dashboardData = {
        tasks: response.data?.data?.tasks || [],
        projects: response.data?.data?.projects || [],
        users: response.data?.data?.users || [],
      };

      return dashboardData;
    } catch (error) {
      console.error('Error fetching dashboard content:', error);
      throw error;
    }
  },

  // Legacy function - Get all dashboard tasks (filtered to exclude archived projects)
  getDashboardTasks: async () => {
    try {
      // Get current session for the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw new Error(`Session error: ${sessionError.message}`);
      }

      if (!session?.access_token) {
        throw new Error('No authentication token available');
      }

      // Get the function URL from the Supabase project
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-dashboard-tasks`;

      // Get the client's current date for consistent timezone handling
      const clientToday = new Date();
      const clientTodayStr = clientToday.toISOString();

      // Call the Edge Function with the client's current date
      const response = await axios.post(
        functionUrl,
        {
          // Pass the client-side today date to ensure consistent behavior with frontend
          todayDate: clientTodayStr,
        },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Return the tasks array, with a fallback to empty array
      const tasksData = response.data?.data || [];

      return tasksData;
    } catch (error) {
      console.error('Error fetching dashboard tasks:', error);
      throw error;
    }
  },

  // Check if a user has dashboard-relevant content
  checkDashboardRelevance: async () => {
    const startTime = performance.now();
    try {
      // Get current session for the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Error getting session:', sessionError);
        throw new Error(`Session error: ${sessionError.message}`);
      }

      if (!session?.access_token) {
        console.error('No access token in session');
        throw new Error('No authentication token available');
      }

      // Get the function URL from the Supabase project
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-dashboard-relevance`;

      // Get the client's current date for consistent timezone handling
      const clientToday = new Date();
      const clientTodayStr = clientToday.toISOString();

      console.log(`[DEBUG] Calling check-dashboard-relevance at ${new Date().toISOString()}`);
      
      // Call the Edge Function with client date information
      const response = await axios.post(
        functionUrl,
        {
          // Pass the client-side today date to ensure consistent behavior with frontend
          todayDate: clientTodayStr,
        },
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      console.log(`[DEBUG] check-dashboard-relevance result:`, {
        hasDashboardContent: response.data.hasDashboardContent,
        executionTimeMs: duration.toFixed(2),
        statusCode: response.status,
        statusText: response.statusText
      });

      return response.data.hasDashboardContent;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      console.error('Error checking dashboard relevance:', error, {
        executionTimeMs: duration.toFixed(2)
      });
      
      // Default to false if there's an error - this will route to projects instead
      return false;
    }
  },
  // Accept an invitation to a project
  acceptInvitation: async (invitationId: string, projectId: string) => {
    // Get current session for the auth token
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      throw new Error(`Session error: ${sessionError.message}`);
    }

    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    // Call the accept-invitation edge function
    const result = await supabase.functions.invoke('accept-invitation', {
      body: {
        invitationId,
        projectId,
      },
    });

    if (result.error) {
      throw new Error(result.error.message || 'Error accepting invitation');
    }

    return result.data;
  },

  // Process all pending invitations for a user
  processAllPendingInvitations: async (email: string) => {
    try {
      console.log('Processing all pending invitations for email:', email);

      // Get current user information
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('Unable to get current user: ' + (userError?.message || 'No user found'));
      }

      // Find all pending invitations for this email (case-insensitive)
      const { data: pendingInvitations, error: invitationsError } = await supabase
        .from('project_invitations')
        .select('id, project_id, invitee_email, role')
        .eq('status', 'pending')
        .ilike('invitee_email', email); // Case-insensitive email matching

      if (invitationsError) {
        throw new Error('Error fetching pending invitations: ' + invitationsError.message);
      }

      console.log(`Found ${pendingInvitations?.length || 0} pending invitations for ${email}`);

      // Explicitly handle the case where pendingInvitations might be null
      const safeInvitations = pendingInvitations || [];

      if (safeInvitations.length === 0) {
        return {
          success: true,
          message: 'No pending invitations found',
          processedCount: 0,
          invitations: [],
        };
      }

      // Process each invitation
      const results = await Promise.all(
        safeInvitations.map(async invitation => {
          try {
            console.log(
              `Processing invitation ${invitation.id} for project ${invitation.project_id}`
            );

            // Use the accept-invitation function for each invitation
            const result = await secureApi.acceptInvitation(invitation.id, invitation.project_id);

            return {
              invitationId: invitation.id,
              projectId: invitation.project_id,
              success: true,
              result,
            };
          } catch (error) {
            console.error(`Error processing invitation ${invitation.id}:`, error);

            // Continue despite errors in individual invitations
            return {
              invitationId: invitation.id,
              projectId: invitation.project_id,
              success: false,
              error: error instanceof Error ? error.message : String(error),
            };
          }
        })
      );

      // Add user as collaborator directly as a fallback
      // This is a safety check in case any invitations failed to process
      // It will try to directly add the user to all projects they were invited to
      for (const invitation of safeInvitations) {
        try {
          const result = results.find(r => r.invitationId === invitation.id);

          // Skip if the invitation was already successfully processed
          if (result && result.success) continue;

          console.log(`Fallback: Directly adding user to project ${invitation.project_id}`);

          // Try adding the user as collaborator directly
          const { error: collaboratorError } = await supabase.from('project_collaborators').insert({
            project_id: invitation.project_id,
            user_id: user.id,
            role: invitation.role || 'guest',
          });

          if (collaboratorError && !collaboratorError.message?.includes('duplicate key')) {
            console.error(
              `Fallback error for project ${invitation.project_id}:`,
              collaboratorError
            );
          }
        } catch (fallbackError) {
          console.error(`Fallback exception for project ${invitation.project_id}:`, fallbackError);
        }
      }

      return {
        success: true,
        message: `Processed ${safeInvitations.length} pending invitations`,
        processedCount: safeInvitations.length,
        results,
        invitations: safeInvitations,
      };
    } catch (error: unknown) {
      console.error('Error processing all pending invitations:', error);
      throw error;
    }
  },

  // Validate if user can downgrade to a specific plan
  validatePlanDowngrade: async (
    targetTier: string
  ): Promise<{ valid: boolean; reason?: string }> => {
    try {
      console.log('Validating plan downgrade to:', targetTier);

      // Get current user ID
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('No authenticated user');
      }

      // Call the Netlify function
      const response = await axios.post('/.netlify/functions/validate-plan-downgrade', {
        userId: user.id,
        targetTier,
      });

      console.log('Validation response:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error validating plan downgrade:', error);

      // Handle axios errors
      if (error && typeof error === 'object' && 'response' in error && error.response?.data) {
        const axiosError = error as {
          response: { data: Record<string, unknown> };
        };

        // If we got a structured response with reason, use it
        if (axiosError.response.data.reason) {
          return {
            valid: false,
            reason: String(axiosError.response.data.reason),
          };
        }

        // Otherwise use the error message
        const errorMessage =
          typeof axiosError.response.data.error === 'string'
            ? axiosError.response.data.error
            : 'Could not validate plan eligibility';

        return { valid: false, reason: errorMessage };
      }

      // For other types of errors
      return {
        valid: false,
        reason: error instanceof Error ? error.message : 'An error occurred during validation',
      };
    }
  },

  // Create a table definition for a topic
  createTableDefinition: async (topicId: string, name?: string) => {
    try {
      console.log('Creating table definition for topic:', topicId);

      // Call the Supabase Edge Function - auth token is automatically handled by the client
      const { data, error } = await supabase.functions.invoke('create-table-definition', {
        body: {
          topic_id: topicId,
          name: name || 'New Table',
        },
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error(error.message || 'Error creating table definition');
      }

      if (!data || !data.success || !data.table_definition) {
        console.error('Invalid response from Edge Function:', data);
        throw new Error('Invalid response from create-table-definition function');
      }

      console.log('Table definition created:', data.table_definition);
      return data;
    } catch (error: unknown) {
      console.error('Error creating table definition:', error);
      throw error;
    }
  },

  // Supabase admin operations
  supabaseAdmin: async (action: string, payload: Record<string, unknown>) => {
    try {
      console.log(`Making secure API call: ${action}`, payload);

      // Check if the action is inviteUserByEmail, which now uses the Supabase Edge Function
      if (action === 'inviteUserByEmail') {
        // Call the Supabase Edge Function directly
        const { data, error } = await supabase.functions.invoke('invite-user', {
          body: {
            email: payload.email,
            projectId: payload.projectId,
            inviterId: payload.inviterId,
            origin: payload.origin,
          },
        });

        if (error) {
          throw new Error(error.message || 'Error calling invite-user function');
        }

        console.log('Supabase function response:', data);
        return data;
      }

      // For all other actions, use the Netlify function as before
      const response = await axios.post(`/.netlify/functions/supabase-admin`, {
        action,
        payload,
      });
      console.log(`Secure API response for ${action}:`, response.data);
      return response.data;
    } catch (error: unknown) {
      console.error(`Secure API error for ${action}:`, error);

      // Check if it's an Axios error with response data
      if (error && typeof error === 'object' && 'response' in error && error.response?.data) {
        const axiosError = error as {
          response: { data: Record<string, unknown> };
          request: unknown;
        };
        const errorMessage =
          (typeof axiosError.response.data.error === 'string' && axiosError.response.data.error) ||
          (typeof axiosError.response.data.message === 'string' &&
            axiosError.response.data.message) ||
          'Server error';

        const serverError = new Error(errorMessage);
        // Preserve the original axios error properties
        Object.assign(serverError, {
          response: axiosError.response,
          request: axiosError.request,
        });
        throw serverError;
      }

      // If it's a network error or other axios error, just rethrow
      throw error;
    }
  },

  // OpenAI operations
  openai: {
    chat: async (action: string, payload: Record<string, unknown>) => {
      const response = await axios.post(`/.netlify/functions/openai-secure`, {
        action,
        payload,
      });
      return response.data;
    },
    transcribe: async (audioData: Blob) => {
      const formData = new FormData();
      formData.append('file', audioData, 'audio.webm');

      const response = await axios.post(`/.netlify/functions/openai-secure`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          action: 'transcribe',
        },
      });
      return response.data;
    },
  },

};
